import qs from 'querystringify';
import {
  PathParser,
  MessageSubscribeRequest,
  SbaasEvent,
  MessageRequestEventListener,
  SbaasSSEConfig,
} from './types';

interface InternalEventListeners {
  onInit: EventListener;
  onRunning: EventListener;
  onCompleted: EventListener;
  onConnectionOpen: (source: EventSource) => EventListener;
  onConnectionError: (source: EventSource) => EventListener;
}

interface MessageEventObject extends InternalEventListeners {
  source: EventSource;
}

interface EventDict {
  [index: number]: MessageEventObject;
}

export const INIT = 'Init';
export const RUNNING = 'Running';
export const COMPLETED = 'Completed';

export class SbaasSSE {
  private events: EventDict = {};
  private base: string;
  private parser: PathParser;

  public constructor(config: SbaasSSEConfig) {
    this.base = config.base;
    this.parser = config.parser;
  }

  public subscribe(
    request: MessageSubscribeRequest | MessageSubscribeRequest[],
  ): EventSource | EventSource[] {
    let sources: EventSource | EventSource[];
    if (Array.isArray(request)) {
      sources = [];
      for (const req of request) {
        sources.push(this.doSubscribe(req));
      }
    } else {
      sources = this.doSubscribe(request);
    }
    return sources;
  }

  public unsubscribe(ticket: number | number[]) {
    let i = 0;
    if (Array.isArray(ticket)) {
      for (const t of ticket) {
        if (this.doUnsubscribe(t)) {
          i++;
        }
      }
    } else if (this.doUnsubscribe(ticket)) {
      i = 1;
    }
    return i;
  }

  private doSubscribe(request: MessageSubscribeRequest): EventSource {
    const url = this.createUrl(request);
    const { ticket, listeners = {}, withCredentials = false } = request;
    const source = new EventSource(url, { withCredentials });
    const _listeners = this.createListeners(ticket, listeners);
    this.events[ticket] = { source, ..._listeners };
    source.onopen = _listeners.onConnectionOpen(source);
    source.onerror = _listeners.onConnectionError(source);
    source.addEventListener(INIT, _listeners.onInit);
    source.addEventListener(RUNNING, _listeners.onRunning);
    source.addEventListener(COMPLETED, _listeners.onCompleted);

    return source;
  }

  private doUnsubscribe(ticket: number) {
    if (this.events.hasOwnProperty(ticket)) {
      this.removeListeners(ticket);
      return true;
    }
    return false;
  }

  private trimSlashes(input: string): string {
    return input.toString().replace(/(^\/+|\/+$)/g, '');
  }

  private createUrl({ endpoint, auth, ticket }: MessageSubscribeRequest) {
    const q = qs.stringify({ auth, ticket });
    const url = this.trimSlashes(`${this.base}/push/${this.parser(endpoint)}`);
    return `${url}?${q}`;
  }

  private createListeners(
    ticket: number,
    listeners: Partial<MessageRequestEventListener>,
  ): InternalEventListeners {
    return {
      onConnectionOpen: source => ev => {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line:no-console
          console.log(
            `SBaaS SSE - Opened connection to ${source.url}`,
            ev,
            source,
          );
        }
        const event: any = ev;
        event.data = {};
        event.status = 'ok';
        if (listeners.onConnectionOpen) {
          listeners.onConnectionOpen(this.payload(event));
        }
      },
      onConnectionError: source => ev => {
        // tslint:disable-next-line:no-console
        console.error(
          `SBaaS SSE - Error occurred in connection to ${source.url}`,
          ev,
          source,
        );
        const event: any = ev;
        event.data = {};
        event.status = 'error';
        if (listeners.onConnectionError) {
          listeners.onConnectionError(this.payload(event));
        }
      },
      onInit: ev => {
        if (ev instanceof MessageEvent) {
          if (process.env.NODE_ENV !== 'production') {
            // tslint:disable-next-line:no-console
            console.log(`SBaaS SSE - Received "Start" event`, ev.data);
          }
          if (listeners.onInit) {
            listeners.onInit(this.payload(ev));
          }
        } else {
          throw new TypeError('Unexpected event object');
        }
      },
      onRunning: ev => {
        if (ev instanceof MessageEvent) {
          if (process.env.NODE_ENV !== 'production') {
            // tslint:disable-next-line:no-console
            console.log(`SBaaS SSE - Received "Running" event`, ev.data);
          }
          if (listeners.onRunning) {
            listeners.onRunning(this.payload(ev));
          }
        } else {
          throw new TypeError('Unexpected event object');
        }
      },
      onCompleted: ev => {
        let mv = true;
        if (ev instanceof MessageEvent) {
          if (process.env.NODE_ENV !== 'production') {
            // tslint:disable-next-line:no-console
            console.log(`SBaaS SSE - Received "Stop" event`, ev.data);
          }
          if (listeners.onCompleted) {
            listeners.onCompleted(this.payload(ev));
          }
        } else {
          mv = false;
        }
        this.removeListeners(ticket);
        if (!mv) {
          throw new TypeError('Unexpected event object');
        }
      },
    };
  }

  private payload(source: MessageEvent): SbaasEvent {
    let status: 'ok' | 'error' = 'ok';
    let body: any;
    if (
      source.data &&
      typeof source.data === 'string' &&
      source.data.length > 0
    ) {
      const data = JSON.parse(source.data);
      status = data.status;
      body = data.body;
    }
    return { source, status, body };
  }

  private removeListeners(ticket: number) {
    const { onInit, onRunning, onCompleted, source } = this.events[ticket];
    source.removeEventListener(INIT, onInit);
    source.removeEventListener(RUNNING, onRunning);
    source.removeEventListener(COMPLETED, onCompleted);
    source.close();
    delete this.events[ticket];
  }
}
